import { useEffect, useRef } from "react";
//Importing sub-components
import styled from "styled-components";
import SkeletonLoader from "../generic/SkeletonLoader";
import SelectControl from "./SelectControl";
import SelectControl_m from './SelectControl_m'
import addHeaders from "../../utils/addHeader";
import IconButton from "../generic/IconButton";
import { Icon_Edit } from "../../styles/icons";

// Setup Use of API
import useApi from '../../hooks/useApi';
import buildQueryString from '../../utils/api/buildQueryString';
import { useQuery } from '@tanstack/react-query'
import useStaleTime from "../../hooks/useStaleTime";
import { qk } from "../../utils/queryKeys";
import { useNavigate } from "react-router-dom";

//setup for language State & profile
import { useSnapshot } from 'valtio';
import useTranslation from "../../hooks/useTranslation";
import useProfile from "../../hooks/useProfile";

import generateSetValueObject, { resetSetValueObject } from "./generateSetValueObject";

//Setting up the Wrapper
const SelectContainer = styled.div`
    width: 100%;
    height: 100%;
    margin: 0;
    position: relative;
`

const RowWrapper = styled.div`
    display: flex;
    flex: 1;
    gap: 1rem;
    align-items: stretch;
    height: 100%;
`



//Select takes an entity like 'unit' a value like {name:'meter',id:'1'} a setter and options object.
const Select = ({ entity, name, valueName, valueId, valueMap, nullDefaults = {}, setValue, options, isRed, testId, defaultType, configDefault, autoFocus, labelId, inputId, entityParams, labelLanguage, linkedFields, onAdvance }) => {

    if (!options) { options = {} }

    //Setting up API 
    const { getMe } = useApi();
    const navigate = useNavigate();
    const staleTime = useStaleTime(entity)

    //Setting up translations 
    const { tState } = useTranslation()
    const snap = useSnapshot(tState)

    //Setting up the profile
    const { pState } = useProfile()
    const profile = useSnapshot(pState)
    const orgCountry = profile.currentOrgCountry

    //Setting up language Variables
    const language = snap.currentLang
    const placeholder = `${entity}.placeholder`
    const modalTitle = `${entity}.modal.title`


    const openList = () => {
        navigate(options.list)
    }

    const queryString = entityParams ? buildQueryString(entityParams, linkedFields) : null;

    // Names come in the org's language unless the config asks for the user's:
    // `labelLanguage: 'ui'` (Studio, whose pickers are about the model, not the
    // org's data). Part of the query key, so switching language refetches.
    const nameLang = labelLanguage === 'ui' ? language : null

    //Api Retrieval function to get list for the Entity
    const getData = async (key) => {
        const queryOrgCountry = key.queryKey[2]
        const extraParams = queryString ? `&${queryString.slice(1)}` : '';
        const langParam = key.queryKey[4] ? `&lang=${encodeURIComponent(key.queryKey[4])}` : ''
        const resource = `${entity}/?country=${queryOrgCountry}${extraParams}${langParam}`
        const response = await getMe(resource)

        //Checks if the endpoint returns a meta.default
        let defaultValue = response?.meta?.default || ''
        if (!defaultValue && options.autoSelectFirst !== false) defaultValue = response.data[0]

        const list = addHeaders(response.data, options.withHeaders)

        //Adds the default value to the data info
        return { list: list, defaultValue: defaultValue }
    }

    //Return Data to Form
    const returnData = (data, valueMap) => {
        const result = generateSetValueObject(data, valueMap, nullDefaults);
        setValue(result);
    }

    //Request to get Select Data from API
    const result = useQuery({
        queryKey: qk.select(entity, orgCountry, queryString || undefined, nameLang || undefined),
        queryFn: getData,
        keepPreviousData: true,
        staleTime: staleTime
    })

    /*
     * The value this field filled in itself, as opposed to one the user picked
     * or the record was saved with. While the field still holds it, a new
     * default replaces it: a line's VAT code is filled the moment the line
     * opens, before a product is picked, and picking a digital service for a
     * customer abroad must then move it to the OSS rate — not leave the
     * untyped line's default behind.
     */
    const autoFilledRef = useRef(null)

    // If the field is empty — or still holds its own default — process the defaultValue.
    // Priority: configDefault (explicit id from cardViewData) > meta.default from API > data[0]
    useEffect(() => {
        const ownDefault = autoFilledRef.current !== null && valueId === autoFilledRef.current
        if (result.isSuccess && (valueName === '' || ownDefault) && entity && options.autoSelectFirst !== false) {
            let defaultItem = result.data.defaultValue;
            if (configDefault) {
                // Match strictly on id — avoids false positives from scanning all field values
                const matched = result.data.list.list.find(item => !item.header && item.id === configDefault);
                if (matched) defaultItem = matched;
            }
            if (!defaultItem) return;
            if (ownDefault && defaultItem.id === valueId) return;
            autoFilledRef.current = defaultItem.id;
            const newValue = generateSetValueObject(defaultItem, valueMap, nullDefaults);
            setValue(newValue);
        }
    }, [result.isSuccess, valueName, valueId, entity, result.data]); // valueMap, setValue, configDefault omitted — static config / stable callbacks

    if (result.isLoading) {
        return <SkeletonLoader config={['F']} compact />
    }

    if (result.isError) {
        return <span>{result.error?.message}</span>
    }

    return (
        <>
            <RowWrapper name="SelectWrapper">
                <SelectContainer name='SelectContainer'>
                    <SelectControl
                        name={name}
                        valueName={valueName}
                        valueId={valueId}
                        setValue={setValue}
                        data={result.data.list}
                        defaultValue={(defaultType === 'none') ? '' : result.data.defaultValue}
                        placeholder={placeholder}
                        onSelect={selection => returnData(selection, valueMap)}
                        entity={entity}
                        options={options}
                        isRed={isRed}
                        testId={testId}
                        autoFocus={autoFocus}
                        labelId={labelId}
                        inputId={inputId}
                        onAdvance={onAdvance}
                    />
                    <SelectControl_m
                        name={name}
                        valueName={valueName}
                        valueId={valueId}
                        setValue={setValue}
                        data={result.data.list}
                        defaultValue={(defaultType === 'none') ? '' : result.data.defaultValue}
                        placeholder={placeholder}
                        onSelect={selection => returnData(selection, valueMap)}
                        entity={entity}
                        modalTitle={modalTitle}
                        options={options}
                        isRed={isRed}
                        testId={testId}
                        labelId={labelId}
                    />

                </SelectContainer>
                {options.edit && <IconButton clickAction={openList} icon={Icon_Edit} />}
            </RowWrapper>
        </>
    )
};

export default Select;
